import React, { useMemo, useState } from 'react';
import redAlertIcon from '@assets/icons/redAlert-icon.svg';
import { useLanguage } from 'Context/LanguageContext';
import { getModifyStatus, validateModifyStatus, getTaskRemoteType } from 'FielderUtils/task/modifyStatus';
import { modifyTaskStatusRequest } from '@core/api/task/task.service';
import { useDispatch } from 'react-redux';
import { Dialog, DialogTitle, DialogContent, DialogActions, FormControl, RadioGroup, FormControlLabel, Radio, TextField, Stack, Typography, Box } from '@mui/material';
import {
  StyleFielderButton,
  StyleFielderButtonOutlined,
  StyleFielderTitleTypography
} from '~/FielderElements/FielderElements.styles';
import { FielderHard } from '@core/theme/baseTheme';

export default function ModifyTaskStatus(props) {
  const lan = useLanguage();
  const dispatch = useDispatch();
  const remoteTaskIds = useMemo(() => getTaskRemoteType(props.taskTypes), [props.taskTypes]);
  const options = useMemo(
    () => getModifyStatus(lan, props.statusTask, props.taskTypeId, remoteTaskIds),
    [lan, props.statusTask, props.taskTypeId, remoteTaskIds]
  );
  const initialStatus = options.find((o) => o.checked)?.value ?? null;
  const [selectedStatus, setSelectedStatus] = useState(initialStatus);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState(false);
  const [statusError, setStatusError] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const validate = () => {
    const cErr = !comment || !comment.trim();
    const sErr = selectedStatus == null;
    setCommentError(cErr);
    setStatusError(sErr);
    return !cErr && !sErr;
  };
  const handleRequestModifyStatus = () => {
    if (!validate()) return;
    const body = {
      taskStatus: selectedStatus,
      idTask: props.idTask,
      dateStatus: Date.now(),
      comment: comment.trim(),
    };
    const loadingConfig = { dispatch };
    const onSuccess = () => {
      setShowErrorMessage(false);
      props.handleCloseModifyStatus(true);
    };
    const onError = ({ status }) => {
      const message = validateModifyStatus(status, lan);
      setErrorMessage(message);
      setShowErrorMessage(true);
    };
    modifyTaskStatusRequest(body, onSuccess, onError, loadingConfig);
  };
  return (
    <Dialog
      open
      onClose={() => props.handleCloseModifyStatus(false)}
      maxWidth="xs"
      fullWidth
      aria-labelledby="modify-task-status-title"
    >
      <DialogTitle id="modify-task-status-title" sx={{ textAlign: 'center' }}>
        <StyleFielderTitleTypography variant={'subtitle2'}>
          {lan.modifyTaskStatus}
        </StyleFielderTitleTypography>
      </DialogTitle>
      <DialogContent dividers>
        {/* Status options */}
        <FormControl component="fieldset" error={statusError} sx={{ width: '100%' }}>
          <RadioGroup
            value={selectedStatus}
            onChange={(e) => {
              setSelectedStatus(e.target.value);
              setStatusError(false);
            }}
          >
            <Stack spacing={1}>
              {options.map((opt) => (
                <FormControlLabel
                  key={opt.key}
                  value={String(opt.value)}
                  control={<Radio />}
                  label={opt.name}
                />
              ))}
            </Stack>
          </RadioGroup>
          {statusError && (
            <Typography variant="body2" sx={{ color: 'error.main', mt: 0.5 }}>
              *{lan.selectAStatus}
            </Typography>
          )}
        </FormControl>
        {/* Comment */}
        <Box sx={{ mt: 3, position: 'relative' }}>
          <TextField
            fullWidth
            multiline
            minRows={4}
            id="modify-status-comment"
            label={lan.reasonForChange}
            placeholder={lan.reasonForChange}
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
              if (commentError) setCommentError(false);
            }}
            error={commentError}
            helperText={commentError ? `*${lan.reasonForChange}` : ' '}
            slotProps={{
              textarea: { maxLength: 255 },
              '&.Mui-focused': {
                color: FielderHard,
              },
            }}
          />
          {commentError && (
            <Box
              component="img"
              src={redAlertIcon}
              alt=""
              sx={{ position: 'absolute', right: 8, top: 8, width: 20, height: 20 }}
            />
          )}
        </Box>
        {/* API error feedback */}
        {showErrorMessage && (
          <Typography variant="body2" sx={{ color: 'error.main', mt: 1 }}>
            *{errorMessage}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", alignItems: "center", p:3 }}>
        <StyleFielderButtonOutlined
          id="cancel-modify"
          variant="outlined"
          onClick={() => props.handleCloseModifyStatus(false)}
          size={"medium"}
        >
          {lan.cancel}
        </StyleFielderButtonOutlined>
        <StyleFielderButton
          id="update-modify-status"
          onClick={handleRequestModifyStatus}
          size={'medium'}
        >
          {lan.modifyStatus}
        </StyleFielderButton>
      </DialogActions>
    </Dialog>
  );
}
